import React, { useState } from 'react';

const FileUpload = ({ onFileUpload, isLoading }) => {
  const [dragActive, setDragActive] = useState(false);
  const [uploadMode, setUploadMode] = useState('single'); // 'single' or 'dual'
  const [engagementFile, setEngagementFile] = useState(null);
  const [viewsFile, setViewsFile] = useState(null);
  const [engagementDragActive, setEngagementDragActive] = useState(false);
  const [viewsDragActive, setViewsDragActive] = useState(false);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleEngagementDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setEngagementDragActive(true);
    } else if (e.type === "dragleave") {
      setEngagementDragActive(false);
    }
  };

  const handleViewsDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setViewsDragActive(true);
    } else if (e.type === "dragleave") {
      setViewsDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      onFileUpload(e.dataTransfer.files[0]);
    }
  };

  const handleEngagementDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setEngagementDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setEngagementFile(e.dataTransfer.files[0]);
    }
  };

  const handleViewsDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setViewsDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setViewsFile(e.dataTransfer.files[0]);
    }
  };

  const handleChange = (e) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      onFileUpload(e.target.files[0]);
    }
  };

  const handleEngagementChange = (e) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      setEngagementFile(e.target.files[0]);
    }
  };

  const handleViewsChange = (e) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      setViewsFile(e.target.files[0]);
    }
  };

  const handleDualUpload = () => {
    if (engagementFile && viewsFile) {
      onFileUpload([engagementFile, viewsFile]);
    }
  };

  const resetDualUpload = () => {
    setEngagementFile(null);
    setViewsFile(null);
  };

  return (
    <div className="card p-8 relative overflow-hidden">
      {/* Magical Background Effects */}
      {isLoading && (
        <>
          <div className="absolute inset-0 bg-gradient-to-r from-blue-50/50 via-indigo-50/30 to-purple-50/50 animate-professional-glow"></div>
          <div className="absolute top-6 right-6 w-2 h-2 bg-blue-500 rounded-full animate-fade-in-out"></div>
          <div className="absolute top-10 right-10 w-1.5 h-1.5 bg-indigo-500 rounded-full animate-fade-in-out" style={{ animationDelay: '0.5s' }}></div>
          <div className="absolute top-14 right-14 w-1 h-1 bg-purple-500 rounded-full animate-fade-in-out" style={{ animationDelay: '1s' }}></div>
        </>
      )}

      {/* Upload Mode Selector */}
      <div className="mb-8 relative z-10">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Choose Upload Mode</h3>
        <div className="flex space-x-4">
          <button
            onClick={() => setUploadMode('single')}
            disabled={isLoading}
            className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
              uploadMode === 'single'
                ? 'bg-blue-600 text-white shadow-lg'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            } ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            📸 Single Screenshot
          </button>
          <button
            onClick={() => setUploadMode('dual')}
            disabled={isLoading}
            className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
              uploadMode === 'dual'
                ? 'bg-blue-600 text-white shadow-lg'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            } ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            👁️ Dual Screenshots (Auto Views)
          </button>
        </div>
        <p className="text-sm text-gray-600 mt-2">
          {uploadMode === 'single' 
            ? 'Upload one screenshot with engagement data (likes, comments, shares)'
            : 'Upload two screenshots: one for engagement data and one for view counts (with eye icon 👁️)'
          }
        </p>
      </div>

      {/* Single Upload Mode */}
      {uploadMode === 'single' && (
        <div className="relative z-10">
          {isLoading ? (
            /* Professional Loading Animation */
            <div className="border-2 border-dashed border-blue-300 rounded-xl p-12 text-center bg-gradient-to-br from-blue-50/80 to-indigo-50/60 animate-professional-glow">
              <div className="space-y-6">
                {/* AI Processing Animation */}
                <div className="mx-auto w-20 h-20 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg">
                  <div className="w-8 h-8 border-4 border-white/30 border-t-white rounded-full animate-spin"></div>
                </div>
                
                <div className="space-y-3">
                  <p className="text-xl font-semibold text-gray-900">
                    AI is analyzing your screenshot...
                  </p>
                  <p className="text-gray-600">
                    Extracting engagement data with advanced AI processing
                  </p>
                </div>
                
                {/* Professional Progress Bar */}
                <div className="relative h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-indigo-600 animate-progress-bar"></div>
                </div>
                
                {/* Status Indicators */}
                <div className="flex justify-center space-x-2">
                  <div className="w-2 h-2 bg-blue-500 rounded-full animate-fade-in-out"></div>
                  <div className="w-2 h-2 bg-indigo-500 rounded-full animate-fade-in-out" style={{ animationDelay: '0.3s' }}></div>
                  <div className="w-2 h-2 bg-purple-500 rounded-full animate-fade-in-out" style={{ animationDelay: '0.6s' }}></div>
                </div>
              </div>
            </div>
          ) : (
            /* Normal Upload Area */
            <div
              className={`relative border-2 border-dashed rounded-xl p-12 text-center transition-all duration-300 group ${
          dragActive
                  ? 'border-blue-500 bg-gradient-to-br from-blue-50 to-indigo-50 scale-105'
                  : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50/50'
              }`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <input
          type="file"
          accept="image/*"
          onChange={handleChange}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              />
              
              <div className="space-y-6">
                <div className="mx-auto w-20 h-20 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <svg className="w-10 h-10 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                
                <div className="space-y-2">
                  <p className="text-xl font-semibold text-gray-900 group-hover:text-blue-600 transition-colors duration-200">
                    {dragActive ? '🎯 Drop your screenshot here' : '📸 Upload Screenshot'}
                  </p>
                  <p className="text-gray-600">
                    {dragActive ? 'Release to upload' : 'Drag and drop your screenshot or click to browse'}
                  </p>
                </div>
                
                <div className="inline-flex items-center px-4 py-2 bg-gray-100 rounded-lg text-sm text-gray-600">
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Supports: JPG, PNG, GIF (Max 10MB)
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Dual Upload Mode */}
      {uploadMode === 'dual' && (
        <div className="space-y-6 relative z-10">
          {isLoading ? (
            /* Professional Dual Loading Animation */
            <div className="border-2 border-dashed border-indigo-300 rounded-xl p-12 text-center bg-gradient-to-br from-indigo-50/80 to-purple-50/60 animate-professional-glow">
              <div className="space-y-6">
                {/* Dual AI Processing Animation */}
                <div className="flex justify-center space-x-8">
                  <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center shadow-lg">
                    <div className="w-6 h-6 border-4 border-white/30 border-t-white rounded-full animate-spin"></div>
                  </div>
                  <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg">
                    <div className="w-6 h-6 border-4 border-white/30 border-t-white rounded-full animate-spin"></div>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <p className="text-xl font-semibold text-gray-900">
                    AI is analyzing both screenshots...
                  </p>
                  <p className="text-gray-600">
                    Processing engagement data and view counts with advanced AI
                  </p>
                </div>
                
                {/* Dual Professional Progress Bars */}
                <div className="space-y-3">
                  <div className="relative h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-r from-green-500 to-emerald-600 animate-progress-bar"></div>
                  </div>
                  <div className="relative h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-r from-purple-500 to-indigo-600 animate-progress-bar" style={{ animationDelay: '0.5s' }}></div>
                  </div>
                </div>
                
                {/* Professional Status Indicators */}
                <div className="flex justify-center space-x-3">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-fade-in-out"></div>
                  <div className="w-2 h-2 bg-emerald-500 rounded-full animate-fade-in-out" style={{ animationDelay: '0.2s' }}></div>
                  <div className="w-2 h-2 bg-purple-500 rounded-full animate-fade-in-out" style={{ animationDelay: '0.4s' }}></div>
                  <div className="w-2 h-2 bg-indigo-500 rounded-full animate-fade-in-out" style={{ animationDelay: '0.6s' }}></div>
                </div>
              </div>
            </div>
          ) : (
            /* Normal Dual Upload Areas */
            <>
              {/* Engagement Screenshot */}
              <div>
                <h4 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                  <span className="mr-2">❤️💬↗️</span>
                  Engagement Screenshot
                </h4>
                <div
                  className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-all duration-300 group ${
                    engagementDragActive
                      ? 'border-green-500 bg-gradient-to-br from-green-50 to-emerald-50 scale-105'
                      : 'border-gray-300 hover:border-green-400 hover:bg-gray-50/50'
                  }`}
                  onDragEnter={handleEngagementDrag}
                  onDragLeave={handleEngagementDrag}
                  onDragOver={handleEngagementDrag}
                  onDrop={handleEngagementDrop}
                >
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleEngagementChange}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
        />
        
        <div className="space-y-4">
                    <div className="mx-auto w-16 h-16 bg-gradient-to-br from-green-100 to-emerald-100 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
            </svg>
          </div>
          
                    <div className="space-y-2">
                      <p className="text-lg font-semibold text-gray-900 group-hover:text-green-600 transition-colors duration-200">
                        {engagementDragActive ? '🎯 Drop engagement screenshot' : '📸 Upload Engagement Screenshot'}
                      </p>
                      <p className="text-sm text-gray-600">
                        {engagementDragActive ? 'Release to upload' : 'Screenshot showing likes ❤️, comments 💬, and shares ↗️'}
                      </p>
                    </div>
                    
                    {engagementFile && (
                      <div className="inline-flex items-center px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm">
                        ✓ {engagementFile.name}
                      </div>
                    )}
                  </div>
          </div>
        </div>
        
              {/* Views Screenshot */}
              <div>
                <h4 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                  <span className="mr-2">👁️</span>
                  Views Screenshot
                </h4>
                <div
                  className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-all duration-300 group ${
                    viewsDragActive
                      ? 'border-purple-500 bg-gradient-to-br from-purple-50 to-violet-50 scale-105'
                      : 'border-gray-300 hover:border-purple-400 hover:bg-gray-50/50'
                  }`}
                  onDragEnter={handleViewsDrag}
                  onDragLeave={handleViewsDrag}
                  onDragOver={handleViewsDrag}
                  onDrop={handleViewsDrop}
                >
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleViewsChange}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  />
                  
                  <div className="space-y-4">
                    <div className="mx-auto w-16 h-16 bg-gradient-to-br from-purple-100 to-violet-100 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
                    </div>
                    
                    <div className="space-y-2">
                      <p className="text-lg font-semibold text-gray-900 group-hover:text-purple-600 transition-colors duration-200">
                        {viewsDragActive ? '🎯 Drop views screenshot' : '👁️ Upload Views Screenshot'}
                      </p>
                      <p className="text-sm text-gray-600">
                        {viewsDragActive ? 'Release to upload' : 'Screenshot showing view count with eye icon 👁️'}
                      </p>
                    </div>
                    
                    {viewsFile && (
                      <div className="inline-flex items-center px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm">
                        ✓ {viewsFile.name}
          </div>
                    )}
          </div>
        </div>
      </div>

              {/* Upload Button */}
              <div className="flex justify-center space-x-4">
                <button
                  onClick={handleDualUpload}
                  disabled={!engagementFile || !viewsFile || isLoading}
                  className={`px-6 py-3 rounded-lg font-medium transition-all duration-200 ${
                    engagementFile && viewsFile && !isLoading
                      ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-lg hover:shadow-xl'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  {isLoading ? 'Analyzing...' : '🚀 Analyze Both Screenshots'}
                </button>
                
                {(engagementFile || viewsFile) && (
                  <button
                    onClick={resetDualUpload}
                    className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-all duration-200"
                  >
                    Reset
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default FileUpload;